/**
 * DateTime & Timezone Formatting Engine
 *
 * Ensures all stored and transferred backend timestamps remain strict UTC/GMT (ISO 8601),
 * while formatting them for display in the user's preferred timezone across the UI.
 */

export interface TimezoneOption {
  value: string;
  label: string;
  region: string;
}

export const COMMON_TIMEZONES: TimezoneOption[] = [
  { value: "UTC", label: "UTC (Coordinated Universal Time / GMT+0)", region: "Global" },
  { value: "Europe/Bucharest", label: "Europe/Bucharest (EET/EEST, GMT+2 / GMT+3)", region: "Europe" },
  { value: "Europe/London", label: "Europe/London (GMT / BST, GMT+0 / GMT+1)", region: "Europe" },
  { value: "Europe/Paris", label: "Europe/Paris (CET/CEST, GMT+1 / GMT+2)", region: "Europe" },
  { value: "Europe/Berlin", label: "Europe/Berlin (CET/CEST, GMT+1 / GMT+2)", region: "Europe" },
  { value: "Europe/Rome", label: "Europe/Rome (CET/CEST, GMT+1 / GMT+2)", region: "Europe" },
  { value: "Europe/Madrid", label: "Europe/Madrid (CET/CEST, GMT+1 / GMT+2)", region: "Europe" },
  { value: "Europe/Athens", label: "Europe/Athens (EET/EEST, GMT+2 / GMT+3)", region: "Europe" },
  { value: "Europe/Kyiv", label: "Europe/Kyiv (EET/EEST, GMT+2 / GMT+3)", region: "Europe" },
  { value: "Europe/Chisinau", label: "Europe/Chisinau (EET/EEST, GMT+2 / GMT+3)", region: "Europe" },
  { value: "Europe/Warsaw", label: "Europe/Warsaw (CET/CEST, GMT+1 / GMT+2)", region: "Europe" },
  { value: "America/New_York", label: "America/New_York (Eastern Time, GMT-5 / GMT-4)", region: "Americas" },
  { value: "America/Chicago", label: "America/Chicago (Central Time, GMT-6 / GMT-5)", region: "Americas" },
  { value: "America/Denver", label: "America/Denver (Mountain Time, GMT-7 / GMT-6)", region: "Americas" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (Pacific Time, GMT-8 / GMT-7)", region: "Americas" },
  { value: "America/Toronto", label: "America/Toronto (Eastern Time, GMT-5 / GMT-4)", region: "Americas" },
  { value: "America/Sao_Paulo", label: "America/Sao_Paulo (BRT, GMT-3)", region: "Americas" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GST, GMT+4)", region: "Asia" },
  { value: "Asia/Jerusalem", label: "Asia/Jerusalem (IST, GMT+2 / GMT+3)", region: "Asia" },
  { value: "Asia/Singapore", label: "Asia/Singapore (SGT, GMT+8)", region: "Asia" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo (JST, GMT+9)", region: "Asia" },
  { value: "Asia/Hong_Kong", label: "Asia/Hong_Kong (HKT, GMT+8)", region: "Asia" },
  { value: "Australia/Sydney", label: "Australia/Sydney (AEST/AEDT, GMT+10 / GMT+11)", region: "Oceania" },
  { value: "Pacific/Auckland", label: "Pacific/Auckland (NZST/NZDT, GMT+12 / GMT+13)", region: "Oceania" },
];

/**
 * Normalizes input into a valid Date object or null
 */
export function toValidDate(input: Date | string | number | null | undefined): Date | null {
  if (!input) return null;
  const d = input instanceof Date ? input : new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Formats a timestamp into a full date and time string using the specified timezone
 */
export function formatDateTime(
  input: Date | string | number | null | undefined,
  timeZone = "UTC",
  options?: Intl.DateTimeFormatOptions
): string {
  const d = toValidDate(input);
  if (!d) return "—";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: timeZone || "UTC",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      ...options,
    }).format(d);
  } catch {
    // Fallback to UTC if timezone is invalid
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      ...options,
    }).format(d);
  }
}

/**
 * Formats a timestamp into a date-only string using the specified timezone
 */
export function formatDate(
  input: Date | string | number | null | undefined,
  timeZone = "UTC",
  options?: Intl.DateTimeFormatOptions
): string {
  const d = toValidDate(input);
  if (!d) return "—";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: timeZone || "UTC",
      year: "numeric",
      month: "short",
      day: "2-digit",
      ...options,
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      year: "numeric",
      month: "short",
      day: "2-digit",
      ...options,
    }).format(d);
  }
}

/**
 * Formats a timestamp into a time-only string using the specified timezone
 */
export function formatTime(
  input: Date | string | number | null | undefined,
  timeZone = "UTC",
  options?: Intl.DateTimeFormatOptions
): string {
  const d = toValidDate(input);
  if (!d) return "—";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: timeZone || "UTC",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      ...options,
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      ...options,
    }).format(d);
  }
}

/**
 * Return strict ISO 8601 UTC representation (e.g. 2026-09-08T13:30:00.000Z)
 */
export function formatIsoUtc(input: Date | string | number | null | undefined): string {
  const d = toValidDate(input);
  return d ? d.toISOString() : "";
}

/**
 * Returns formatted timezone label with current offset
 */
export function getTimezoneLabel(timeZone: string): string {
  const found = COMMON_TIMEZONES.find((t) => t.value === timeZone);
  if (found) return found.label;
  return timeZone;
}
